// Copyright 2020 Sorama B.V.

import { PLATFORM } from "aurelia-pal";
import { GuidanceTopicType, IGuidanceTopic } from "catalogs/GuidanceCatalog";
import { WorkflowModuleType } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowModes } from "services/WorkflowModes/WorkflowModes";

export class MeasuringGuidanceCatalog {
    static MeasuringOverviewModule: IGuidanceTopic = {
        name: "Measuring_Overview",
        modulePath: PLATFORM.moduleName("guidance/Measuring/Overview.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicType: GuidanceTopicType.TopicOverview,
        topicContent: "Measuring Help",
    };
    static MeasuringGS1Module: IGuidanceTopic = {
        name: "Measuring_Q1", // https://sorama.atlassian.net/wiki/spaces/UI/pages/716308507/M1+What+is+the+effect+of+position+and+distance+when+measuring+with+the+Acoustic+Camera
        modulePath: PLATFORM.moduleName("guidance/Measuring/M1EffectOfPositionDistanceMeasuring.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is the effect of position and distance when measuring?",
        catchwords: [""],
    };
    static MeasuringGS2Module: IGuidanceTopic = {
        name: "Measuring_Q2", // https://sorama.atlassian.net/wiki/spaces/UI/pages/716079298/M2+Why+do+I+need+a+reference+background+noise+measurement
        modulePath: PLATFORM.moduleName("guidance/Measuring/M2WhyReferenceMeasurement.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Why do I need a reference (background noise) measurement?",
        catchwords: ["Background noise"],
    };
    static MeasuringGS3Module: IGuidanceTopic = {
        name: "Measuring_Q3", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720240723/M3+Should+I+do+a+measurement+in+Far+field+or+Near+field
        modulePath: PLATFORM.moduleName("guidance/Measuring/M3MeasurementInFarfieldOrNearfield.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Should I do a measurement in Far field or Near field?",
        catchwords: [""],
    };
    static MeasuringGS4Module: IGuidanceTopic = {
        name: "Measuring_Q4", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720240965/M4+How+silent+should+my+environment+be+for+a+good+measurement
        modulePath: PLATFORM.moduleName("guidance/Measuring/M4SilentEnvironmentForGoodMeasurement.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How silent should my environment be for a good measurement?",
        catchwords: ["Reference measurement", "Resolution", "Noise floor"],
    };
    static MeasuringGS5Module: IGuidanceTopic = {
        name: "Measuring_Q5", // https://sorama.atlassian.net/wiki/spaces/UI/pages/719849047/M5+What+is+the+directionality+of+the+microphones+on+the+array
        modulePath: PLATFORM.moduleName("guidance/Measuring/M5DirectionalityOfMicrophonesArray.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is the directionality of the microphones on the array?",
        catchwords: [""],
    };
    static MeasuringGS6Module: IGuidanceTopic = {
        name: "Measuring_Q6", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175621/M6+What+is+a+Scanning+measurement
        modulePath: PLATFORM.moduleName("guidance/Measuring/M6WhatIsaScanningMeasurement.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is a ‘Scanning’ measurement?",
        catchwords: [""],
    };
    static MeasuringGS7Module: IGuidanceTopic = {
        name: "Measuring_Q7", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175639/M7+When+should+I+also+use+a+Class+1+sound+level+meter
        modulePath: PLATFORM.moduleName("guidance/Measuring/M7WhenToUseClass1SoundLevelMeter.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "When should I also use a Class 1 sound level meter?",
        catchwords: [""],
    };
    static MeasuringGS8Module: IGuidanceTopic = {
        name: "Measuring_Q8", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175648/M8+Do+temperature+and+wind+influence+my+measurements
        modulePath: PLATFORM.moduleName("guidance/Measuring/M8TemperatureAndWindInfluenceMeasurements.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Do temperature and wind influence my measurements?",
        catchwords: ["Wind rumble", "Low frequencies", "Wind shielding"],
        hidden: true,
    };
    static MeasuringGS9Module: IGuidanceTopic = {
        name: "Measuring_Q9", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175775/M9+How+do+I+prepare+for+a+measurement
        modulePath: PLATFORM.moduleName("guidance/Measuring/M9HowDoIPrepareForAMeasurement.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How do I prepare for a measurement?",
        catchwords: ["Steel tape measure", "Streaming", "Position Sorama CAM", "Good measurement practice"],
    };
    static MeasuringGS10Module: IGuidanceTopic = {
        name: "Measuring_Q10", // https://sorama.atlassian.net/wiki/spaces/UI/pages/745080162/M10+What+is+a+custom+measurement
        modulePath: PLATFORM.moduleName("guidance/Measuring/M10WhatISCustomMeasurement.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is a 'custom' measurement?",
        catchwords: [""],
    };
    static MeasuringGS11Module: IGuidanceTopic = {
        name: "Measuring_Q11", //https://sorama.atlassian.net/wiki/spaces/UI/pages/825426066/M11+Measuring+Quick+Start
        modulePath: PLATFORM.moduleName("guidance/Measuring/M11MeasuringQuickStart.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicType: GuidanceTopicType.TopicOverview,
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicContent: "Measuring Quick Start",
    };
    static MeasuringGS12Module: IGuidanceTopic = {
        name: "Measuring_Q12", //https://sorama.atlassian.net/wiki/spaces/UI/pages/825327775/M12+What+is+Microphone+Selection
        modulePath: PLATFORM.moduleName("guidance/Measuring/M12WhatIsMicrophoneSelection.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicType: GuidanceTopicType.TopicOverview,
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicContent: "What is Microphone Selection?",
    };

    static MeasuringGS13Module: IGuidanceTopic = {
        name: "Measuring_Q13", // https://sorama.atlassian.net/wiki/spaces/UI/pages/826277903/M13+How+do+I+make+a+near+field+background+image
        modulePath: PLATFORM.moduleName("guidance/Measuring/M13HowDoIMakeaNearFieldBackgroundImage.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How do I make a near field background image?",
        catchwords: [""],
    };
    static MeasuringGS14Module: IGuidanceTopic = {
        name: "Measuring_Q14", // https://sorama.atlassian.net/wiki/spaces/POR/pages/1560150190/Measurement+Practice
        modulePath: PLATFORM.moduleName("guidance/Measuring/M14MeasurementPractice.html"),
        title: "Measuring",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canUseCAM64AndCAM1K),
        topicParent: MeasuringGuidanceCatalog.MeasuringOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Measurement Practice",
        catchwords: [""],
    };
}
